import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import dayjs from 'dayjs';
import '../../Analytics.css';
import { getUserTeamIds, ROLES } from '../../utils/roleUtils';
import MetricCard from '../../components/analytics/MetricCard';
import PieCard from '../../components/analytics/PieCard';
import ChartCard from '../../components/analytics/ChartCard';
import UserBreakdownCard from '../../components/analytics/UserBreakdownCard';
import AnalyticsFilters from '../../components/analytics/AnalyticsFilters';

const COLORS = ['#e65c00', '#36d576'];

// RIA AI identifiers - user IDs and phone numbers associated with RIA
const RIA_USER_IDS = ['703', 703]; // User ID for RIA calls
const RIA_PHONE_NUMBERS = ['40373800880', '+40373800880'];

// Helper to check if a user is the RIA AI
const isRiaUser = (userId, userName) => {
  // Check by user ID first (most reliable)
  if (userId && RIA_USER_IDS.includes(userId)) {
    return true;
  }
  if (userId && RIA_USER_IDS.includes(String(userId))) {
    return true;
  }
  
  // Check by phone number in userName or userId
  if (userName || userId) {
    const str = String(userName || userId).replace(/\s+/g, '');
    if (RIA_PHONE_NUMBERS.some(num => str.includes(num) || str === num)) {
      return true;
    }
  }
  
  return false;
};
const FILTERS = [
  { label: 'Today', value: 'today' },
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: 'Custom', value: 'custom' },
];

function getDateRange(filter) {
  const now = dayjs(); // Use local time for user-friendly ranges
  let range;
  if (filter === 'today') {
    range = [now.startOf('day'), now.endOf('day')];
  } else if (filter === '7d') {
    // Ensure we include today by using the current moment for end time
    range = [now.subtract(6, 'day').startOf('day'), now.endOf('day')];
  } else if (filter === '30d') {
    range = [now.subtract(29, 'day').startOf('day'), now.endOf('day')];
  } else {
    range = [null, null];
  }
  
  return range;
}

export default function AdminAnalyticsPage() {
  const { supabase, userProfile } = useAuth();
  const [filter, setFilter] = useState('today');
  const [customRange, setCustomRange] = useState([null, null]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    total: 0,
    avgDuration: 0,
    outboundAvgDuration: 0,
    inbound: 0,
    outbound: 0,
    inboundMinutes: 0,
    outboundMinutes: 0,
    perDay: [],
    perHour: [],
  });
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  
  // User breakdown data (now comes from RPC)
  const [userBreakdownData, setUserBreakdownData] = useState([]);

  // No frontend filtering needed - backend already filters based on role
  // Users and teams from filtered endpoints are pre-filtered by role

  useEffect(() => {
    // Wait for userProfile to be fully loaded before fetching users and teams
    if (!userProfile || !userProfile.id) {
      return;
    }
    
    async function fetchUsersAndTeams() {
      // Double check userProfile.id exists before making API calls
      if (!userProfile?.id) {
        console.warn('[ANALYTICS] Skipping users/teams fetch - userProfile.id not available');
        return;
      }
      
      setUsersLoading(true);
      setTeamsLoading(true);
      try {
        // Fetch users and teams with role-based filtering
        const [usersResponse, teamsResponse] = await Promise.all([
          fetch(`/api/users/filtered?userId=${userProfile.id}`),
          fetch(`/api/teams/filtered?userId=${userProfile.id}`)
        ]);
        
        const usersResult = await usersResponse.json();
        const teamsResult = await teamsResponse.json();
        
        if (usersResult.success) {
          setUsers(usersResult.data || []);
        }
        setUsersLoading(false);
        
        if (teamsResult.success) {
          setTeams(teamsResult.data || []);
        }
        setTeamsLoading(false);
      } catch (error) {
        // Fallback to direct database queries (with role-based filtering in SQL)
        const { data: usersData } = await supabase.from('imported_users').select('id, full_name, team_id, role');
        const { data: teamsData } = await supabase.from('teams').select('id, name');
        setUsers(usersData || []);
        setTeams(teamsData || []);
        setUsersLoading(false);
        setTeamsLoading(false);
      }
    }
    fetchUsersAndTeams();
  }, [supabase, userProfile]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    
    // Get consistent date range for both main analytics and user breakdown
    const dateRange = filter === 'custom' ? (customRange || [null, null]) : getDateRange(filter);
    let [start, end] = dateRange;
    
    if (!start || !end) {
      setLoading(false);
      return;
    }
    
    // Store the original dayjs objects for user breakdown
    const originalStart = start;
    const originalEnd = end;
    
    start = dayjs(start).startOf('day').toISOString();
    end = dayjs(end).endOf('day').toISOString();
    
    // Determine team IDs for RPC based on role
    let teamIdsForRpc = null;
    if (userProfile?.role === ROLES.TEAM_LEAD) {
      // Team leads can see data from all their teams by default
      const teamIds = getUserTeamIds(userProfile);
      
      if (teamIds.length > 0) {
        // If team filter is selected, use it; otherwise show all their teams
        if (selectedTeams && selectedTeams.length > 0) {
          teamIdsForRpc = selectedTeams;
        } else {
          teamIdsForRpc = teamIds;
        }
      } else {
        // If team lead has no teams, show no data
        setData({ total: 0, avgDuration: 0, outboundAvgDuration: 0, inbound: 0, outbound: 0, inboundMinutes: 0, outboundMinutes: 0, perDay: [], perHour: [] });
        setLoading(false);
        return;
      }
    } else if (userProfile?.role === ROLES.ADMIN) {
      // Admins can filter by teams if selected
      if (selectedTeams && selectedTeams.length > 0) {
        teamIdsForRpc = selectedTeams;
      }
    }
    
    // Use the RPC function that includes user breakdown data - single source of truth
    const { data: analyticsResult, error } = await supabase.rpc('get_call_analytics_with_users', {
      start_date: start,
      end_date: end,
      user_role: userProfile?.role || 'admin',
      user_team_id: null, // Deprecated - use selected_team_ids instead
      selected_user_ids: selectedUsers && selectedUsers.length > 0 ? selectedUsers : null,
      selected_team_ids: teamIdsForRpc // Pass the correct team IDs based on role and selection
    });

    if (error) {
      setLoading(false);
      setData({ total: 0, avgDuration: 0, outboundAvgDuration: 0, inbound: 0, outbound: 0, inboundMinutes: 0, outboundMinutes: 0, perDay: [], perHour: [] });
      return;
    }

    const analytics = analyticsResult || {};
    
    // Set user breakdown data from RPC response
    // Transform RIA entries to show as "AI_RIA"
    if (analytics.user_breakdown) {
      const transformedBreakdown = analytics.user_breakdown.map(user => {
        // Check if this user is the RIA AI (by userId or phone number)
        if (isRiaUser(user.userId, user.userName)) {
          return {
            ...user,
            userName: 'AI_RIA',
            isRia: true
          };
        }
        return user;
      });
      setUserBreakdownData(transformedBreakdown);
    }
    
    // Determine if we need weekly grouping for display
    const isLongPeriod = filter === '30d' || (filter === 'custom' && customRange && dayjs(customRange[1]).diff(dayjs(customRange[0]), 'days') > 14);
    
    // Process daily breakdown for charts
    let perDay = analytics.daily_breakdown || [];
    
    if (isLongPeriod && perDay.length > 0) {
      // Group daily data into weeks for 30D display
      const weeklyMap = {};
      
      perDay.forEach(day => {
        const weekStart = dayjs(day.date).startOf('week');
        const weekKey = weekStart.format('YYYY-MM-DD'); // Use simple date format instead of week number
        const weekLabel = `Week ${weekStart.format('MMM D')}`;
        
        if (!weeklyMap[weekKey]) {
          weeklyMap[weekKey] = {
            day: weekLabel,
            sortKey: weekKey,
            Inbound: 0,
            Outbound: 0,
            totalDuration: 0,
            totalCalls: 0,
            avgDuration: 0
          };
        }
        
        weeklyMap[weekKey].Inbound += day.Inbound;
        weeklyMap[weekKey].Outbound += day.Outbound;
        weeklyMap[weekKey].totalCalls += day.total_calls;
        weeklyMap[weekKey].totalDuration += (day.avg_duration * day.total_calls);
      });
      
      // Calculate weekly averages
      Object.values(weeklyMap).forEach(week => {
        week.avgDuration = week.totalCalls > 0 ? Math.round(week.totalDuration / week.totalCalls) : 0;
      });
      
      perDay = Object.values(weeklyMap).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    } else {
      // For 7D and shorter, ensure all days in range are included
      const [rangeStart, rangeEnd] = filter === 'custom' ? customRange : getDateRange(filter);
      const allDaysMap = {};
      
      // Initialize all days in the range with 0 values
      if (rangeStart && rangeEnd) {
        let currentDate = dayjs(rangeStart);
        const endDate = dayjs(rangeEnd);
        
        while (currentDate.isBefore(endDate, 'day') || currentDate.isSame(endDate, 'day')) {
          const dayKey = currentDate.format('ddd');
          const dateKey = currentDate.format('YYYY-MM-DD');
          
          allDaysMap[dateKey] = {
            day: dayKey,
            date: dateKey,
            Inbound: 0,
            Outbound: 0,
            avg_duration: 0,
            total_calls: 0,
            avgDuration: 0,
            totalCalls: 0,
            sortKey: dateKey
          };
          
          currentDate = currentDate.add(1, 'day');
        }
      }
      
      // Populate with actual data from RPC
      perDay.forEach(day => {
        const dateKey = dayjs(day.date).format('YYYY-MM-DD');
        if (allDaysMap[dateKey]) {
          allDaysMap[dateKey] = {
            ...allDaysMap[dateKey],
            ...day,
            avgDuration: day.avg_duration || 0,
            totalCalls: day.total_calls || 0
          };
        }
      });
      
      // Convert to sorted array
      perDay = Object.values(allDaysMap).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    }

    // Process hourly breakdown - convert to average calls per hour per day
    const hourlyData = analytics.hourly_breakdown || [];
    const dailyHourlyMap = {};
    
    // Group hourly data by day and calculate averages
    hourlyData.forEach(hour => {
      const dayKey = dayjs(hour.date).format('ddd');
      
      if (!dailyHourlyMap[dayKey]) {
        dailyHourlyMap[dayKey] = {
          hour: dayKey,
          totalInbound: 0,
          totalOutbound: 0,
          hourCount: 0
        };
      }
      
      dailyHourlyMap[dayKey].totalInbound += hour.Inbound;
      dailyHourlyMap[dayKey].totalOutbound += hour.Outbound;
      dailyHourlyMap[dayKey].hourCount++;
    });
    
    // Calculate average calls per hour for each day
    const perHour = Object.values(dailyHourlyMap).map(day => ({
      hour: day.hour,
      Inbound: day.hourCount > 0 ? Math.round(day.totalInbound / 24) : 0, // Avg inbound per hour for this day
      Outbound: day.hourCount > 0 ? Math.round(day.totalOutbound / 24) : 0 // Avg outbound per hour for this day
    }));

    setData({
      total: analytics.total_calls || 0,
      avgDuration: analytics.avg_duration || 0,
      outboundAvgDuration: analytics.outbound_avg_duration || 0, // Use RPC outbound average
      inbound: analytics.inbound_calls || 0,
      outbound: analytics.outbound_calls || 0,
      inboundMinutes: analytics.total_inbound_minutes || 0, // Total inbound duration in minutes
      outboundMinutes: analytics.total_outbound_minutes || 0, // Total outbound duration in minutes
      zeroSecondInbound: analytics.zero_second_inbound || 0,
      zeroSecondOutbound: analytics.zero_second_outbound || 0,
      perDay,
      perHour: analytics.hourly_breakdown || [] // Use RPC hourly data directly
    });
    
    // User breakdown data is now included in the RPC response above
    setLoading(false);
  }, [supabase, filter, customRange, selectedUsers, selectedTeams, userProfile]);

  // Helper function to get formatted date range for display
  const getFormattedDateRange = useCallback(() => {
    // Show loading state when custom filter is selected but no dates picked
    if (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1])) {
      return '...';
    }
    
    let [start, end] = filter === 'custom' ? customRange : getDateRange(filter);
    if (!start || !end) return 'Invalid range';
    
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    
    if (filter === 'today') {
      return startDate.format('MMM D');
    } else if (startDate.isSame(endDate, 'month')) {
      return `${startDate.format('MMM D')} - ${endDate.format('D')}`;
    } else {
      return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`;
    }
  }, [filter, customRange]);

  // Initial fetch on component mount and when refresh is triggered
  // Wait for userProfile to be fully loaded before fetching analytics
  useEffect(() => {
    if (!userProfile || !userProfile.id) {
      return;
    }
    fetchAnalytics();
  }, [fetchAnalytics, lastRefresh, userProfile]);

  // Auto-refresh effect - REMOVED to prevent twitching
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setLastRefresh(Date.now());
  //   }, 30000); // 30 seconds
  //   return () => clearInterval(interval);
  // }, []);

  const isTeamLead = userProfile?.role === ROLES.TEAM_LEAD;
  const isAdmin = userProfile?.role === ROLES.ADMIN;
  
  // Format duration helper
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format minutes to "Xh Ym" format
  const formatMinutesToHours = (totalMinutes) => {
    if (!totalMinutes || totalMinutes === 0) return '0m';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2>{isTeamLead ? 'Team Analytics' : 'Admin Analytics'}</h2>
        <p>{isTeamLead ? 'View analytics for your team.' : 'View and filter analytics for all users and teams.'}</p>
        <AnalyticsFilters
          filter={filter}
          setFilter={setFilter}
          customRange={customRange}
          setCustomRange={setCustomRange}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          selectedTeams={selectedTeams}
          setSelectedTeams={setSelectedTeams}
          users={users}
          teams={teams}
          onRefresh={() => setLastRefresh(Date.now())}
          isAdmin={isAdmin}
          isTeamLead={isTeamLead}
          usersLoading={usersLoading}
          teamsLoading={teamsLoading}
        />
      </div>
      
      {/* Main Grid Layout */}
      <div className="analytics-main-grid">
        {/* Total Calls with mini chart */}
        <MetricCard
          icon={
            <svg className="analytics-metric-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
          }
          title="Total Calls"
          value={data.total}
          description="Inbound and Outbound combined"
          chartData={data.perDay}
          chartKey="totalCalls"
          chartColor="#3b82f6"
          loading={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1]))}
        />

        {/* Average Outbound Call Duration with mini chart */}
        <MetricCard
          icon={
            <svg className="analytics-metric-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          }
          title="Average Outbound Duration"
          value={data.outboundAvgDuration}
          description="Outbound calls only (excludes 0s calls)"
          chartData={data.perDay}
          chartKey="avgDuration"
          chartColor="#f97316"
          loading={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1]))}
          formatter={formatDuration}
        />

        {/* Inbound vs Outbound Pie Chart with 0-second breakdown */}
        <PieCard
          icon={
            <svg className="analytics-metric-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11 2v20c-5.07-.5-9-4.79-9-10s3.93-9.5 9-10zm2.03 0v8.99H22c-.47-4.74-4.24-8.52-8.97-8.99zm0 11.01V22c4.74-.47 8.5-4.25 8.97-8.99h-8.97z"/>
            </svg>
          }
          title="Inbound vs Outbound"
          data={[
            { name: 'Inbound', value: data.inbound },
            { name: 'Outbound', value: data.outbound },
          ]}
          breakdown={{
            inbound: {
              zeroSecond: data.zeroSecondInbound || 0,
              nonZero: (data.inbound || 0) - (data.zeroSecondInbound || 0)
            },
            outbound: {
              zeroSecond: data.zeroSecondOutbound || 0,
              nonZero: (data.outbound || 0) - (data.zeroSecondOutbound || 0)
            }
          }}
          colors={COLORS}
          loading={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1]))}
        />

        {/* Total Inbound Minutes */}
        <MetricCard
          icon={
            <svg className="analytics-metric-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
            </svg>
          }
          title="Total Inbound Minutes"
          value={data.inboundMinutes}
          description={`${data.inbound} inbound calls`}
          chartData={data.perDay}
          chartKey="Inbound"
          chartColor="#10b981"
          loading={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1]))}
          formatter={formatMinutesToHours}
        />

        {/* Total Outbound Minutes */}
        <MetricCard
          icon={
            <svg className="analytics-metric-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
          }
          title="Total Outbound Minutes"
          value={data.outboundMinutes}
          description={`${data.outbound} outbound calls`}
          chartData={data.perDay}
          chartKey="Outbound"
          chartColor="#e65c00"
          loading={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1]))}
          formatter={formatMinutesToHours}
        />

        {/* Bottom Row Charts */}
        <div className="analytics-bottom-row">
          {/* Calls per Day */}
          <ChartCard
            icon={
              <svg className="analytics-chart-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
              </svg>
            }
            title={filter === '30d' || (filter === 'custom' && customRange && dayjs(customRange[1]).diff(dayjs(customRange[0]), 'days') > 14) 
              ? 'Calls per week' 
              : 'Calls per day'}
            metricValue={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1])) ? '...' : (() => {
              const [start, end] = filter === 'custom' ? customRange : getDateRange(filter);
              if (!start || !end) return '0 avg';
              
              const totalDays = dayjs(end).diff(dayjs(start), 'days') + 1;
              const avgCallsPerDay = totalDays > 0 ? Math.round(data.total / totalDays) : 0;
              return `${avgCallsPerDay} avg`;
            })()}
            metricPeriod={getFormattedDateRange()}
            data={data.perDay}
            type="bar"
            colors={COLORS}
            loading={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1]))}
            stacked={true}
          />

          {/* Calls per Hour */}
          <ChartCard
            icon={
              <svg className="analytics-chart-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            }
            title="Calls per hour"
            metricValue={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1])) ? '...' : (() => {
              const [start, end] = filter === 'custom' ? customRange : getDateRange(filter);
              if (!start || !end) return '0 avg';
              
              const totalHours = dayjs(end).diff(dayjs(start), 'hours') + 1;
              const avgCallsPerHour = totalHours > 0 ? Math.round(data.total / totalHours) : 0;
              return `${avgCallsPerHour} avg`;
            })()}
            metricPeriod={getFormattedDateRange()}
            data={data.perHour}
            type="line"
            colors={COLORS}
            loading={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1]))}
          />

          {/* Call Trends */}
          <ChartCard
            icon={
              <svg className="analytics-chart-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
              </svg>
            }
            title="Call Trends"
            metricValue={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1])) ? '...' : (data.total > 0 ? '+' + Math.round((data.outbound / data.total) * 100) + '%' : '0%')}
            metricPeriod="This period"
            data={data.perDay}
            type="line"
            colors={['#3b82f6', '#f97316']}
            loading={loading || (filter === 'custom' && (!customRange || !customRange[0] || !customRange[1]))}
          />
        </div>
        
        {/* User Breakdown Card - Full Width at Bottom */}
        <UserBreakdownCard
          data={userBreakdownData}
          loading={loading}
        />
      </div>
    </div>
  );
} 